import { useState, useRef, useEffect } from "react";
import { usePageSEO } from "@/hooks/usePageSEO";
import profAvatar from "@/assets/Prof_1.webp";
import { Send, ArrowLeft, Sparkles, Loader2, LogIn, Crown, BookOpen, HelpCircle, FileText, Lightbulb, ClipboardList, Camera, Mic, MicOff, X, Volume2, Download } from "lucide-react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { RichMessageRenderer } from "@/components/RichMessageRenderer";
import { StudentResponseCard } from "@/components/chat/StudentResponseCard";
import ChatActionButtons from "@/components/chat/ChatActionButtons";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAuthModal } from "@/hooks/useAuthModal";
import { supabase } from "@/integrations/supabase/client";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useIsPremium } from "@/hooks/useIsPremium";
import PaymentMethodModal from "@/components/PaymentMethodModal";
import PremiumUpsellModal, { type UpsellTrigger } from "@/components/PremiumUpsellModal";
import { useGuide } from "@/hooks/useGuide";
import { useVoiceSync } from "@/hooks/useVoiceSync";
import VoicePlaybackControls from "@/components/chat/VoicePlaybackControls";
import SyncedMessageRenderer from "@/components/chat/SyncedMessageRenderer";
import { buildChatCacheKey, readChatCache, writeChatCache, deleteChatCache } from "@/lib/notionChatHistory";
import { downloadChatAsPdf } from "@/lib/chatPdf";
import { speak } from "@/lib/speak";
import { extractFirstName, buildWelcomeMessage } from "@/lib/nameUtils";

type Msg = { role: "user" | "assistant"; content: string };
type ChatMode = "student" | "parent-coach";
type PendingImage = { id: string; dataUrl: string; name: string };
type ChatSubscription = { plan: string; limit: number; used: number; photoUsed: number; photoLimit: number | null };
type StreamResult = {
  error?: string;
  limitReached?: boolean;
  paymentRequired?: boolean;
  photoLimitReached?: boolean;
};
type AssistantMeta = {
  subject?: string;
  questionAsked?: boolean;
  question?: string;
  expectedAnswer?: string;
  studentAnswerEvaluated?: boolean;
  answerCorrect?: boolean;
  scoreIncrement?: {
    total?: number;
    correct?: number;
  };
  actions?: string[];
};

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
const META_REGEX = /<kelasai_meta>([\s\S]*?)<\/kelasai_meta>/i;

const extractAssistantMeta = (content: string): AssistantMeta | null => {
  const match = content.match(META_REGEX);
  if (!match) return null;

  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
};

const stripAssistantMeta = (content: string) => content.replace(META_REGEX, "").trim();

// Strip <think>...</think> reasoning blocks (complete and incomplete)
const THINK_COMPLETE_REGEX = /<think>[\s\S]*?<\/think>/gi;
const THINK_OPEN_REGEX = /<think>[\s\S]*$/i;
const stripThinking = (content: string): string => {
  let cleaned = content.replace(THINK_COMPLETE_REGEX, "");
  cleaned = cleaned.replace(THINK_OPEN_REGEX, "");
  return cleaned;
};

const parseAssistantSections = (content: string) => {
  const visibleContent = stripAssistantMeta(content);
  // Strip any legacy "## Explication" / "Explication" header the model might still emit
  const cleaned = visibleContent
    .replace(/^\s*##\s*Explication\s*\n?/i, "")
    .replace(/\n##\s*Explication\s*\n?/i, "\n");

  const qMatch = cleaned.match(/##\s*Question\s*([\s\S]*)$/i);
  if (!qMatch) {
    return { explanation: cleaned.trim(), question: "" };
  }
  return {
    explanation: cleaned.slice(0, qMatch.index).trim(),
    question: qMatch[1]?.trim() ?? "",
  };
};

async function streamChat({ messages, onDelta, onDone, level, schoolClass, country, userId, chatMode, childName, childSubjects, token, images, language, dateOfBirth, plan, goals, specialty, studyLanguage, subjects, notionId, subject }: {
  messages: Msg[]; onDelta: (d: string) => void; onDone: () => void;
  level?: string; schoolClass?: string; country?: string; userId?: string;
  chatMode?: ChatMode; childName?: string; childSubjects?: string[]; token?: string | null; images?: string[]; language?: string; dateOfBirth?: string | null; plan?: string;
  goals?: string | null; specialty?: string | null; studyLanguage?: string | null; subjects?: string[] | null;
  notionId?: string | null; subject?: string | null;
}): Promise<StreamResult | void> {
  // Token is optional — guest/demo mode uses anon key
  const authToken = token || null;

  // ===== Latency tracking (TTFT / total) =====
  const _t0 = performance.now();
  let _ttftMs: number | null = null;

  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    body: JSON.stringify({ messages, level, schoolClass, country, userId, chatMode, childName, childSubjects, images, language, dateOfBirth, plan, goals, specialty, studyLanguage, subjects, notionId, subject }),
  });

  const contentType = resp.headers.get("content-type") || "";

  if (resp.ok && contentType.includes("application/json")) {
    const data = await resp.json().catch(() => ({}));
    if (data.paymentRequired) {
      toast.error(data.error || "Crédits IA insuffisants.");
      return { paymentRequired: true, error: data.error };
    }
  }

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    if (data.limitReached) return { limitReached: true, error: data.error };
    if (data.photoLimitReached) return { photoLimitReached: true, error: data.error };
    if (resp.status === 429) {
      toast.error(data.error || "Trop de requêtes, réessaie dans un moment.");
      return { error: data.error };
    }
    if (resp.status === 402) {
      toast.error(data.error || "Crédits IA insuffisants.");
      return { paymentRequired: true, error: data.error };
    }
    const errorMessage = data.error || "Erreur du service IA";
    toast.error(errorMessage);
    return { error: errorMessage };
  }

  if (!resp.body) throw new Error("No response body");
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done = false;

  while (!done) {
    const { done: d, value } = await reader.read();
    if (d) break;
    if (_ttftMs === null && value && value.byteLength > 0) {
      _ttftMs = Math.round(performance.now() - _t0);
      console.log(`[chat][TTFT] client_ttft_ms=${_ttftMs} mode=${chatMode || "learner"} plan=${plan || "free"}`);
    }
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf("\n")) !== -1) {
      let line = buf.slice(0, idx);
      buf = buf.slice(idx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || !line.trim() || !line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") { done = true; break; }
      try {
        const c = JSON.parse(json).choices?.[0]?.delta?.content;
        if (c) onDelta(c);
      } catch {
        buf = line + "\n" + buf;
        break;
      }
    }
  }
  if (buf.trim()) {
    for (let raw of buf.split("\n")) {
      if (!raw || !raw.startsWith("data: ")) continue;
      const json = raw.replace(/\r$/, "").slice(6).trim();
      if (json === "[DONE]") continue;
      try { const c = JSON.parse(json).choices?.[0]?.delta?.content; if (c) onDelta(c); } catch {}
    }
  }
  onDone();
  // Fire-and-forget metric insert (TTFT + total)
  if (_ttftMs !== null) {
    const _totalMs = Math.round(performance.now() - _t0);
    try {
      supabase.from("ai_latency_metrics").insert({
        source: "client",
        user_id: userId || null,
        ttft_ms: _ttftMs,
        total_ms: _totalMs,
        plan: plan || null,
        chat_mode: chatMode || null,
      } as never).then(() => {});
    } catch { /* ignore */ }
  }
}

const resizeImageToDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const maxSize = 1400;
        const ratio = Math.min(1, maxSize / Math.max(image.width, image.height));
        canvas.width = Math.round(image.width * ratio);
        canvas.height = Math.round(image.height * ratio);
        const context = canvas.getContext("2d");

        if (!context) {
          reject(new Error("Canvas non disponible"));
          return;
        }

        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      image.onerror = () => reject(new Error("Image invalide"));
      image.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error("Lecture impossible"));
    reader.readAsDataURL(file);
  });

const quickActions = [
  { icon: HelpCircle, label: "Poser une question", prefix: "" },
  { icon: BookOpen, label: "Expliquer simplement", prefix: "Explique-moi simplement : " },
  { icon: Lightbulb, label: "Donner un exemple", prefix: "Donne-moi un exemple concret de : " },
  { icon: FileText, label: "Faire un résumé", prefix: "Fais-moi un résumé de : " },
  { icon: ClipboardList, label: "Corriger un exercice", prefix: "Corrige cet exercice : " },
];

const suggestedQuestions = [
  "Trace la courbe de f(x) = x² - 3x + 2 📈",
  "Dessine un triangle rectangle et explique Pythagore 📐",
  "Explique la photosynthèse avec un schéma 🌿",
  "Montre-moi une carte de l'Empire romain 🗺️",
];

const MODE_PROMPTS: Record<string, (subject: string) => string> = {
  exercise: (subject) => `Donne-moi un exercice ciblé en ${subject}, adapté à mon niveau, puis corrige-moi étape par étape.`,
  lesson: (subject) => `Donne-moi une nouvelle leçon claire et structurée en ${subject}, avec exemples simples et mini exercice.`,
  exam: (subject) => `Fais-moi une simulation d'examen en ${subject}, adaptée à mon niveau, avec plusieurs questions.`,
};

const buildAutoPrompt = (searchParams: URLSearchParams) => {
  const message = searchParams.get("message");
  if (message) return message;

  const subject = searchParams.get("subject");
  const topic = searchParams.get("topic");
  const mode = searchParams.get("mode");

  if (topic && subject) {
    return `Explique-moi ${topic} en ${subject} avec une réponse pédagogique, des exemples simples et un petit exercice.`;
  }

  if (mode && subject && MODE_PROMPTS[mode]) {
    return MODE_PROMPTS[mode](subject);
  }

  if (subject) {
    return `Aide-moi à réviser ${subject} à mon niveau avec un premier point essentiel à comprendre.`;
  }

  return null;
};

const Chat = () => {
  usePageSEO({
    title: "Discuter avec KelasAI — IA éducative",
    description: "Pose tes questions à KelasAI, ton tuteur IA personnalisé. Explications, exercices et corrections en temps réel.",
  });

  // Précharger les backgrounds du chat pour éviter les flashs
  useEffect(() => {
    const preloadImages = ["/chat-bg-light.png", "/chat-bg-dark.png"];
    preloadImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);
  const { user, session } = useAuth();
  const { openLogin, openSignup } = useAuthModal();
  const GUEST_LIMIT = 7;
  const GUEST_KEY = "kelasai_guest_msg_count";
  const GUEST_TS_KEY = "kelasai_guest_msg_reset_at";
  const GUEST_WINDOW_MS = 24 * 60 * 60 * 1000;
  const readGuestCount = () => {
    if (typeof window === "undefined") return 0;
    try {
      const ts = Number(localStorage.getItem(GUEST_TS_KEY) || 0);
      if (!ts || Date.now() - ts >= GUEST_WINDOW_MS) {
        localStorage.setItem(GUEST_KEY, "0");
        localStorage.setItem(GUEST_TS_KEY, String(Date.now()));
        return 0;
      }
      return Number(localStorage.getItem(GUEST_KEY) || 0);
    } catch {
      return 0;
    }
  };
  const [guestCount, setGuestCount] = useState<number>(() => readGuestCount());
  const guestLimitReached = !user && guestCount >= GUEST_LIMIT;
  const { language, voice, voiceEnabled: rawVoiceEnabled } = useAppSettings();
  const { isPremium } = useIsPremium();
  const voiceEnabled = rawVoiceEnabled && isPremium;
  const { trackMilestone } = useGuide();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<{ level?: string; school_class?: string; country?: string; avatar_url?: string; role?: string; full_name?: string; subjects?: string[]; date_of_birth?: string | null; goals?: string | null; specialty?: string | null; study_language?: string | null } | null>(null);
  const [parentChildProfile, setParentChildProfile] = useState<{ full_name: string; level: string; school_class: string; country: string; subjects: string[] } | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [parentChildLoaded, setParentChildLoaded] = useState(false);
  const [subscription, setSubscription] = useState<ChatSubscription | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [upsellTrigger, setUpsellTrigger] = useState<UpsellTrigger>("limit_reached");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [activeQuickAction, setActiveQuickAction] = useState<string | null>(null);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [lastSpokenText, setLastSpokenText] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const lastAssistantRef = useRef<HTMLDivElement>(null);
  const autoSentRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const chatMode: ChatMode = searchParams.get("mode") === "parent-coach" ? "parent-coach" : "student";
  const chatCacheTopic = searchParams.get("topic") || searchParams.get("message") || searchParams.get("mode");

  // Clé de cache locale pour l'historique du chat (par notion ou par conversation).
  const chatCacheKey = (() => {
    const conv = searchParams.get("conversation");
    return buildChatCacheKey({
      userId: user?.id,
      conversationId: conv,
      subject: searchParams.get("subject"),
      topic: chatCacheTopic,
    });
  })();
  const cacheKeyRef = useRef(chatCacheKey);
  cacheKeyRef.current = chatCacheKey;
  const [hydratedCacheKey, setHydratedCacheKey] = useState<string | null>(null);

  // Smart auto-scroll: only follow stream if user is near bottom
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [autoFollow, setAutoFollow] = useState(true);

  const isNearBottom = () => {
    const el = scrollContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const handleScroll = () => {
    setAutoFollow(isNearBottom());
  };

  const scrollToBottom = (smooth = true) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  // Voice sync system
  const voiceSync = useVoiceSync(voice, language);

  const stopSpeaking = () => {
    voiceSync.stop();
  };

  const replaySpeech = () => {
    if (lastSpokenText) voiceSync.replay(lastSpokenText);
  };

  // Play any specific assistant message
  const playMessage = (content: string) => {
    voiceSync.startPlayback(stripAssistantMeta(content));
    setLastSpokenText(stripAssistantMeta(content));
  };

  const syncScoreFromAssistant = async (assistantContent: string) => {
    if (!user || chatMode !== "student") return;

    const meta = extractAssistantMeta(assistantContent);
    const subject = meta?.subject?.trim().toLowerCase();
    const totalIncrement = meta?.scoreIncrement?.total ?? 0;
    const correctIncrement = meta?.scoreIncrement?.correct ?? 0;

    if (!subject || !meta?.studentAnswerEvaluated || totalIncrement <= 0) return;

    const { data: existing, error: fetchError } = await supabase
      .from("subject_scores")
      .select("id, total_questions, correct_answers")
      .eq("user_id", user.id)
      .eq("subject", subject)
      .maybeSingle();

    if (fetchError) {
      console.warn("Score fetch error:", fetchError);
      return;
    }

    const totalQuestions = (existing?.total_questions ?? 0) + totalIncrement;
    const correctAnswers = (existing?.correct_answers ?? 0) + correctIncrement;
    const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    const payload = {
      user_id: user.id,
      subject,
      total_questions: totalQuestions,
      correct_answers: correctAnswers,
      score,
      last_activity_at: new Date().toISOString(),
    };

    const { error } = existing
      ? await supabase.from("subject_scores").update(payload).eq("id", existing.id)
      : await supabase.from("subject_scores").insert(payload as any);

    if (error) {
      console.warn("Score update error:", error);
      return;
    }

    if (meta.answerCorrect) {
      toast.success("Bonne réponse comptabilisée dans ton score !");
    }
  };

  useEffect(() => {
    if (!user) {
      setProfileLoaded(true);
      setParentChildLoaded(true);
      return;
    }
    // Authenticated user: reset guest demo counter
    setGuestCount(0);
    try { localStorage.removeItem(GUEST_KEY); } catch {}

    setProfileLoaded(false);
    setParentChildLoaded(false);

    supabase.from("profiles").select("level, avatar_url, country, school_class, role, full_name, subjects, date_of_birth, goals, specialty, study_language").eq("id", user.id).single().then(async ({ data }) => {
      if (data) {
        const profile = data as any;
        setUserProfile(profile);

        if (profile.role === "parent") {
          const requestedChildId = searchParams.get("childId");
          let childId = requestedChildId;

          if (!childId) {
            const { data: links } = await supabase
              .from("parent_child_links")
              .select("child_id")
              .eq("parent_id", user.id)
              .eq("status", "active")
              .limit(1);

            childId = links?.[0]?.child_id;
          }

          if (childId) {
            const { data: child } = await (supabase as any)
              .from("apprenants")
              .select("full_name, level, school_class, country, subjects")
              .eq("id", childId)
              .single();

            if (child) setParentChildProfile(child as any);
          }

          setParentChildLoaded(true);
        } else {
          setParentChildProfile(null);
          setParentChildLoaded(true);
        }
      }

      setProfileLoaded(true);
    });
    supabase.from("user_subscriptions").select("plan, daily_message_limit, messages_used_today, last_reset_date, photos_used_today, last_photo_reset_date").eq("user_id", user.id).single().then(({ data }) => {
      if (data) {
        const d = data as any;
        const today = new Date().toISOString().split("T")[0];
        const used = d.last_reset_date === today ? d.messages_used_today : 0;
        const photoUsed = d.last_photo_reset_date === today ? d.photos_used_today : 0;
        const isPremiumPlan = d.plan === "premium";
        setSubscription({ plan: d.plan, limit: d.daily_message_limit, used, photoUsed, photoLimit: isPremiumPlan ? null : 3 });
        setLimitReached(!isPremiumPlan && used >= d.daily_message_limit);
        if (!isPremiumPlan && used >= d.daily_message_limit) setShowPremiumModal(true);
      }
    });
  }, [user, searchParams]);

  // Stop voice on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop?.();
      voiceSync.stop();
    };
  }, []);

  // Auto-play: when the voice assistant is enabled, start reading the first
  // assistant response of the conversation as soon as the chat is ready.
  const autoGreetedRef = useRef(false);
  useEffect(() => {
    if (autoGreetedRef.current) return;
    if (!voiceEnabled) return;
    if (!profileLoaded) return;
    if (isLoading) return;
    const firstAssistant = messages.find((m) => m.role === "assistant" && m.content?.trim());
    if (!firstAssistant) return;
    autoGreetedRef.current = true;
    playMessage(firstAssistant.content);
  }, [voiceEnabled, profileLoaded, isLoading, messages]);

  // Guest mode: purge le cache du chat dès que l'utilisateur quitte la page.
  useEffect(() => {
    const isGuest = !user;
    if (!isGuest) return;

    const handleBeforeUnload = () => {
      try { deleteChatCache(cacheKeyRef.current); } catch {}
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      try { deleteChatCache(cacheKeyRef.current); } catch {}
    };
  }, [user]);

  // Stop voice when navigating away from chat
  useEffect(() => {
    voiceSync.stop();
  }, [location.pathname]);

  // Time tracker for subject-based progress (counts time spent chatting in a subject)
  useEffect(() => {
    const subj = (searchParams.get("subject") || "").trim().toLowerCase();
    if (!subj || !user) return;
    let stopFn: (() => void) | null = null;
    let cancelled = false;
    (async () => {
      const { startTimeTracker } = await import("@/lib/progressTracker");
      if (cancelled) return;
      stopFn = startTimeTracker(subj);
    })();
    return () => { cancelled = true; stopFn?.(); };
  }, [searchParams, user]);

  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    // User just sent → always scroll to bottom (force-follow)
    if (lastMsg.role === "user") {
      setAutoFollow(true);
      requestAnimationFrame(() => scrollToBottom(true));
      return;
    }
    // Assistant streaming → follow instantly without repeated smooth-scroll animations.
    if (autoFollow) {
      scrollToBottom(false);
    }
  }, [messages]);

  useEffect(() => {
    const prompt = buildAutoPrompt(searchParams);
    const autoKey = searchParams.toString();
    const waitingForStudentContext = !!user && !profileLoaded;
    const waitingForParentContext = chatMode === "parent-coach" && !!user && !parentChildLoaded;

    if (!prompt || !autoKey || hydratedCacheKey !== chatCacheKey || autoSentRef.current === autoKey || isLoading || waitingForStudentContext || waitingForParentContext) return;

    autoSentRef.current = autoKey;
    void send(prompt);
  }, [searchParams, chatCacheKey, hydratedCacheKey, isLoading, user, profileLoaded, parentChildLoaded, chatMode]);

  // Hydratation : restaure les messages depuis le cache local instantanément,
  // puis depuis la base si une conversation explicite est passée en URL.
  useEffect(() => {
    const conv = searchParams.get("conversation");
    const cached = readChatCache(chatCacheKey);
    if (cached?.messages?.length) {
      setMessages(cached.messages);
      if (cached.conversationId) setConversationId(cached.conversationId);
      else if (conv) setConversationId(conv);
      // Empêche d'auto-renvoyer le prompt par défaut quand on rouvre une notion.
      autoSentRef.current = searchParams.toString();
    } else {
      setMessages([]);
      setConversationId(conv || null);
    }
    setHydratedCacheKey(chatCacheKey);

    if (conv && user) {
      // Synchronise depuis la base pour disposer du chat complet et à jour.
      supabase
        .from("messages")
        .select("role, content, created_at")
        .eq("conversation_id", conv)
        .order("created_at", { ascending: true })
        .then(({ data }) => {
          if (!data || data.length === 0) return;
          const restored: Msg[] = data.map((m: any) => ({ role: m.role, content: m.content }));
          setMessages(restored);
          setConversationId(conv);
          autoSentRef.current = searchParams.toString();
          writeChatCache(chatCacheKey, {
            messages: restored,
            conversationId: conv,
            subject: searchParams.get("subject"),
            topic: chatCacheTopic,
            title: cached?.title || null,
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatCacheKey, user?.id]);

  // Sauvegarde locale différée : évite d'écrire dans localStorage à chaque token streamé.
  useEffect(() => {
    if (messages.length === 0) return;
    const timeout = window.setTimeout(() => {
      writeChatCache(cacheKeyRef.current, {
        messages,
        conversationId,
        subject: searchParams.get("subject"),
        topic: chatCacheTopic,
        title:
          messages.find((m) => m.role === "user")?.content.slice(0, 80) ||
          searchParams.get("topic") ||
          searchParams.get("subject") ||
          null,
      });
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [messages, conversationId, searchParams]);

  const saveMessage = async (convId: string, msg: Msg) => {
    if (!user) return;
    await supabase.from("messages").insert({ conversation_id: convId, user_id: user.id, role: msg.role, content: msg.content } as any);
  };

  const handlePickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!user) {
      toast.error("Connecte-toi pour utiliser les photos");
      return;
    }

    const available = subscription?.photoLimit === null
      ? files.length
      : Math.max(0, (subscription?.photoLimit ?? 0) - (subscription?.photoUsed ?? 0) - pendingImages.length);

    if (available <= 0) {
      toast.error("Mode free : 3 photos maximum par jour.");
      return;
    }

    const acceptedFiles = Array.from(files).slice(0, available);
    const processed = await Promise.all(
      acceptedFiles.map(async (file, index) => ({
        id: `${file.name}-${Date.now()}-${index}`,
        name: file.name,
        dataUrl: await resizeImageToDataUrl(file),
      })),
    );

    setPendingImages((prev) => [...prev, ...processed]);

    if (acceptedFiles.length < files.length) {
      toast.error("Le quota photo gratuit a été atteint.");
    }
  };

  const removePendingImage = (id: string) => {
    setPendingImages((prev) => prev.filter((image) => image.id !== id));
  };

  const toggleVoiceInput = () => {
    const SpeechRecognitionApi = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionApi) {
      toast.error("La dictée vocale n'est pas disponible sur cet appareil.");
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop?.();
      return;
    }

    const recognition = new SpeechRecognitionApi();
    recognition.lang = language;
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => {
      setIsListening(false);
      toast.error("Impossible d'activer le micro.");
    };
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0]?.transcript ?? "")
        .join(" ")
        .trim();
      setInput(transcript);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const send = async (text: string) => {
    const imagesToSend = [...pendingImages];
    const trimmed = text.trim();
    if ((!trimmed && imagesToSend.length === 0) || isLoading) return;
    if (limitReached) { setShowPremiumModal(true); return; }
    if (guestLimitReached) {
      toast.error(`Mode démo limité à ${GUEST_LIMIT} questions. Crée un compte gratuit pour continuer.`);
      openSignup();
      return;
    }

    const remainingPhotoQuota = subscription?.photoLimit === null
      ? Number.POSITIVE_INFINITY
      : Math.max(0, (subscription?.photoLimit ?? 0) - (subscription?.photoUsed ?? 0));

    if (imagesToSend.length > remainingPhotoQuota) {
      toast.error("Mode free : quota photo journalier atteint.");
      return;
    }

    const promptText = trimmed || "Analyse cette photo et explique-la selon mon niveau.";
    const userMsg: Msg = {
      role: "user",
      content: `${promptText}${imagesToSend.length > 0 ? `\n\n📷 ${imagesToSend.length} photo(s) jointe(s)` : ""}`,
    };
    setMessages((p) => [...p, userMsg]);
    if (!user) {
      const next = guestCount + 1;
      setGuestCount(next);
      try {
        localStorage.setItem(GUEST_KEY, String(next));
        if (!localStorage.getItem(GUEST_TS_KEY)) {
          localStorage.setItem(GUEST_TS_KEY, String(Date.now()));
        }
      } catch {}
    }
    setInput("");
    setPendingImages([]);
    setActiveQuickAction(null);
    setIsLoading(true);

    // Track AI chat activity (cumulative progress)
    try {
      const trackedSubject = (searchParams.get("subject") || "").trim().toLowerCase();
      const trackedNotionId = (searchParams.get("notionId") || "").trim() || null;
      if (trackedSubject && user) {
        const { trackActivity } = await import("@/lib/progressTracker");
        void trackActivity({
          subject: trackedSubject,
          notionId: trackedNotionId,
          type: "ai_chat",
          value: 1,
          metadata: {
            conversationId: conversationId || null,
            summary: promptText.slice(0, 140),
          },
        });
        const trackedMode = (searchParams.get("mode") || "").trim().toLowerCase();
        if (trackedMode === "exercise") {
          void trackActivity({ subject: trackedSubject, notionId: trackedNotionId, type: "exercise_started", value: 1, metadata: { source: "chat_exercise" } });
        }
      }
    } catch {}

    // Fire conversation creation in background — don't block streaming
    let convId = conversationId;
    const convPromise = (user && !convId)
      ? supabase.from("conversations").insert({ user_id: user.id, title: promptText.slice(0, 60) } as any).select("id").single().then(({ data }) => {
          if (data) { convId = (data as any).id; setConversationId(convId); }
        })
      : Promise.resolve();

    // Save user message in background after conversation is ready
    convPromise.then(() => { if (user && convId) saveMessage(convId, userMsg); });

    let assistantRaw = "";
    // Start voice sync streaming mode
    if (voiceEnabled) voiceSync.startStreaming();

    const upsert = (chunk: string) => {
      assistantRaw += chunk;
      const visible = stripThinking(assistantRaw).trim();
      if (!visible) return;
      setMessages((p) => {
        const last = p[p.length - 1];
        if (last?.role === "assistant") return p.map((m, i) => (i === p.length - 1 ? { ...m, content: visible } : m));
        return [...p, { role: "assistant", content: visible }];
      });
      // Feed streamed text to voice sync for progressive TTS
      if (voiceEnabled) voiceSync.updateStreamText(visible);
    };

    try {
      // Use the session already loaded by AuthProvider — avoid an extra auth call before streaming.
      const cachedToken = session?.access_token || null;

      const result = await streamChat({
        messages: [...messages, userMsg],
        onDelta: upsert,
        level: chatMode === "parent-coach" ? parentChildProfile?.level : userProfile?.level,
        schoolClass: chatMode === "parent-coach" ? parentChildProfile?.school_class : userProfile?.school_class,
        country: chatMode === "parent-coach" ? parentChildProfile?.country : userProfile?.country,
        userId: user?.id,
        token: cachedToken,
        images: imagesToSend.map((image) => image.dataUrl),
        chatMode,
        childName: parentChildProfile?.full_name,
        childSubjects: parentChildProfile?.subjects,
        language,
        dateOfBirth: userProfile?.date_of_birth,
        plan: subscription?.plan,
        goals: userProfile?.goals ?? null,
        specialty: userProfile?.specialty ?? null,
        studyLanguage: userProfile?.study_language ?? null,
        subjects: userProfile?.subjects ?? null,
        notionId: searchParams.get("notionId") || null,
        subject: searchParams.get("subject") || null,
        onDone: () => {
        },
      });

      if (result && (result as any).limitReached) {
        setLimitReached(true);
        setShowPremiumModal(true);
        setIsLoading(false);
        setMessages((p) => [...p, { role: "assistant", content: "⚠️ **Ton crédit de 5 messages est épuisé !**\n\n⏳ Tes messages se régénèrent automatiquement dans les **24 heures**. Reviens demain ou passe en **Premium** pour un accès illimité. 🚀" }]);
        return;
      }

      if (result && (result as any).photoLimitReached) {
        setIsLoading(false);
        setMessages((p) => [...p, { role: "assistant", content: "⚠️ **Quota photo atteint en mode free.**\n\nPasse en **Premium** pour envoyer des photos en illimité. 📷" }]);
        return;
      }

      if (result && (result as any).paymentRequired) {
        setIsLoading(false);
        return;
      }

      if (result && (result as any).error) {
        setIsLoading(false);
        return;
      }

      const assistantSoFar = stripThinking(assistantRaw).trim();
      if (!assistantSoFar) {
        setIsLoading(false);
        return;
      }

      // Don't block UI — fire score sync and message save in background
      syncScoreFromAssistant(assistantSoFar);
      // Finalize voice sync with complete text
      if (voiceEnabled) voiceSync.finishStreaming(assistantSoFar);
      setLastSpokenText(assistantSoFar);
      setIsLoading(false);

      // Track guide milestones
      try {
        const userMsgCount = messages.filter(m => m.role === "user").length + 1;
        if (userMsgCount === 1) trackMilestone("first_chat");
        if (userMsgCount === 5) trackMilestone("five_chats");
        if (imagesToSend.length > 0) trackMilestone("first_photo");
      } catch {}

      // Save assistant message after conversation is ready
      convPromise.then(() => { if (user && convId) saveMessage(convId, { role: "assistant", content: assistantSoFar }); });
      if (subscription) {
        const newUsed = subscription.used + 1;
        const newPhotoUsed = subscription.photoUsed + imagesToSend.length;
        setSubscription({ ...subscription, used: newUsed, photoUsed: newPhotoUsed });
        if (subscription.plan === "free") {
          if (newUsed >= subscription.limit) {
            setLimitReached(true);
            setUpsellTrigger("limit_reached");
            setShowPremiumModal(true);
          } else if (newUsed === 3) {
            // Contextual upsell after 3rd question
            setUpsellTrigger("after_3_questions");
            setShowPremiumModal(true);
          }
        }
      }
    } catch {
      setIsLoading(false);
    }
  };

  const submitStudentAnswer = async (answer: string) => {
    await send(answer);
  };

  const userAvatar = userProfile?.avatar_url || "👦";
  const userAvatarIsImage = typeof userAvatar === "string" && userAvatar.startsWith("http");
  const headerTitle = chatMode === "parent-coach" ? "Coach Parent KelasAI" : "KelasAI Assistant";
  const headerSubtitle = chatMode === "parent-coach"
    ? parentChildProfile
      ? `${parentChildProfile.full_name} · ${parentChildProfile.level}${parentChildProfile.school_class ? ` · ${parentChildProfile.school_class}` : ""}`
      : "Conseils personnalisés pour accompagner votre enfant"
    : userProfile?.role !== "parent" && userProfile?.role !== "enseignant" && userProfile?.level
      ? `${userProfile.level}${userProfile.school_class ? ` · ${userProfile.school_class}` : ""}`
      : "En ligne · Prêt à t'aider";
  const emptyTitle = chatMode === "parent-coach" ? "Bonjour ! Je suis votre coach parent" : "Salut ! Je suis KelasAI";
  const emptyDescription = chatMode === "parent-coach"
    ? "Posez vos questions pour mieux accompagner votre enfant dans son travail, sa motivation et son organisation."
    : "Pose-moi n'importe quelle question, je t'aide à apprendre !";
  const inputPlaceholder = chatMode === "parent-coach"
    ? "Ex. Comment aider mon enfant à progresser en mathématiques ?"
    : "📸 Photo de devoir · 💡 Explication · 📝 Quiz · ❓ Question...";
  const remainingPhotos = subscription?.photoLimit === null ? "∞" : Math.max(0, (subscription?.photoLimit ?? 0) - (subscription?.photoUsed ?? 0));

  const handleDownloadPdf = async () => {
    if (messages.length === 0) {
      toast.error("Pas encore de conversation à exporter.");
      return;
    }
    const subject = searchParams.get("subject") || undefined;
    const topic = searchParams.get("topic") || undefined;
    const baseTitle = topic
      ? `${topic}${subject ? ` — ${subject}` : ""}`
      : subject
        ? `Chat ${subject}`
        : (messages.find((m) => m.role === "user")?.content.slice(0, 60) || "Conversation KelasAI");
    const downloaded = await downloadChatAsPdf({
      title: baseTitle,
      subtitle: subject && topic ? `Notion · ${topic}` : subject || undefined,
      studentName: chatMode === "parent-coach" ? parentChildProfile?.full_name : userProfile?.full_name,
      messages,
    });
    if (!downloaded) {
      toast.error("Aucun contenu lisible à exporter.");
      return;
    }
    toast.success(downloaded === "saved" ? "PDF téléchargé" : "Téléchargement PDF lancé");
  };

  return (
    <div className="flex flex-col h-dvh bg-background">
      {/* Header */}
      <header className="px-4 py-3 border-b border-border bg-card flex items-center gap-3">
        <Link
          to={user ? (searchParams.get("subject") ? `/subject/${searchParams.get("subject")}` : "/dashboard") : "/"}
          className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted hover:bg-muted/80 transition-colors"
          title={searchParams.get("subject") ? `Retour à la matière` : "Retour"}
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </Link>
        <img src={profAvatar} alt="KelasAI" className="w-10 h-10 rounded-2xl object-cover" />
        <div className="flex-1">
          <p className="text-base font-black text-foreground">{headerTitle}</p>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <p className="text-sm text-muted-foreground font-semibold">{headerSubtitle}</p>
          </div>
        </div>
        {!user && (
          <button onClick={openLogin} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors">
            <LogIn className="w-3.5 h-3.5" />
            Connexion
          </button>
        )}
        {user && subscription?.plan === "premium" && (
          <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-bold bg-accent/20 text-accent-foreground">
            <Crown className="w-3.5 h-3.5" /> Premium
          </span>
        )}
        {messages.length > 0 && (
          <button
            onClick={handleDownloadPdf}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-bold text-foreground bg-muted hover:bg-muted/70 transition-colors"
            title="Télécharger la conversation en PDF"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">PDF</span>
          </button>
        )}
        <Sparkles className="w-5 h-5 text-muted-foreground" />
      </header>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto py-4 md:py-6 px-0 space-y-4 relative scroll-smooth chat-bg"
      >
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-6 text-center">
            <div className="text-6xl">🎓</div>
            <div>
              <h2 className="text-2xl font-black text-foreground mb-2">{emptyTitle}</h2>
              <p className="text-base text-muted-foreground font-medium md:text-lg">{emptyDescription}</p>
              {!user && <p className="text-xs text-muted-foreground mt-2"><button onClick={openLogin} className="text-primary font-bold hover:underline">Connecte-toi</button> pour sauvegarder tes conversations</p>}
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-2 justify-center max-w-lg">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button key={action.label} onClick={() => {
                    if (action.prefix) { setInput(action.prefix); setActiveQuickAction(action.label); }
                  }}
                    className="flex items-center gap-1.5 px-3 py-2.5 rounded-2xl text-sm font-bold bg-primary/10 text-primary hover:bg-primary/20 transition-colors border border-primary/20 min-h-[44px]">
                    <Icon className="w-4 h-4" />
                    {action.label}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg w-full">
              {(chatMode === "parent-coach"
                ? [
                    "Comment accompagner mon enfant sans le stresser ?",
                    "Quelle routine mettre en place après l’école ?",
                    "Comment l’aider s’il manque de motivation ?",
                    "Que dire quand il bloque sur un exercice ?",
                  ]
                : suggestedQuestions).map((q) => (
                <button key={q} onClick={() => send(q)} className="px-4 py-3 rounded-2xl text-sm sm:text-base font-semibold text-left bg-muted hover:bg-muted/70 text-foreground transition-colors border border-border min-h-[48px]">{q}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          const isLastAssistant = msg.role === "assistant" && i === messages.map((m, idx) => m.role === "assistant" ? idx : -1).filter(idx => idx !== -1).pop();
          return (
          <div key={i} ref={isLastAssistant ? lastAssistantRef : undefined} className={`flex gap-3 px-3 md:px-4 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center text-lg flex-shrink-0"
              style={{ background: msg.role === "user" ? "hsl(var(--primary) / 0.12)" : "var(--gradient-hero)" }}>
              {msg.role === "user" ? (
                userAvatarIsImage ? (
                  <img
                    src={userAvatar}
                    alt="Avatar utilisateur"
                    className="h-full w-full rounded-2xl object-cover"
                    loading="lazy"
                  />
                ) : (
                  userAvatar
                )
              ) : <img src={profAvatar} alt="KelasAI" className="h-full w-full rounded-2xl object-cover" />}
            </div>
            {msg.role === "assistant" ? (
              <div className="flex flex-col flex-1 min-w-0 max-w-[96%]">
                <div className="edu-chat-bubble max-w-full rounded-tl-sm text-foreground"
                  style={{ background: "hsl(var(--muted))", boxShadow: "none" }}>
                  <div className="edu-chat-readable">
                    {(() => {
                    const meta = extractAssistantMeta(msg.content);
                    const { explanation, question } = parseAssistantSections(msg.content);

                    const bravoBlock = meta?.answerCorrect ? (
                      <div className="flex items-center gap-2 rounded-2xl bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 px-3 py-2 text-green-800 dark:text-green-300">
                        <span className="text-xl">🎉</span>
                        <span className="text-sm font-bold">Bravo, bonne réponse !</span>
                      </div>
                    ) : meta?.studentAnswerEvaluated && meta?.answerCorrect === false ? (
                      <div className="flex items-center gap-2 rounded-2xl bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 px-3 py-2 text-orange-800 dark:text-orange-300">
                        <span className="text-xl">💪</span>
                        <span className="text-sm font-bold">Pas tout à fait, mais continue !</span>
                      </div>
                    ) : null;

                    const isLastMsg = i === messages.length - 1;
                    const voiceActive = isLastMsg && voiceSync.syncState.isPlaying;

                    if (chatMode !== "student" || !question) {
                      return <>{bravoBlock}<SyncedMessageRenderer content={stripAssistantMeta(msg.content)} currentSegmentIndex={voiceSync.syncState.currentSegmentIndex} isVoicePlaying={voiceActive} /></>;
                    }

                    return (
                      <div className="space-y-3">
                        {bravoBlock}
                        {explanation && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">Réponse</p>
                            <RichMessageRenderer content={explanation} />
                          </div>
                        )}

                        <div className="rounded-2xl border border-primary/20 bg-background/80 p-3">
                           <p className="mb-1.5 text-xs font-black uppercase tracking-[0.18em] text-primary">Question IA</p>
                          <RichMessageRenderer content={(() => {
                            // Strip MCQ options from the question text since they'll be shown in StudentResponseCard
                            const lines = question.split("\n");
                            const filtered = lines.filter(line => {
                              const stripped = line.replace(/\*\*/g, "").replace(/__/g, "").replace(/`/g, "").trim();
                              return !stripped.match(/^(?:[-*]\s*)?(?:\[[ xX]\]\s*)?[A-D]\s*[).:-]\s*.+$/i);
                            });
                            return filtered.join("\n").trim();
                          })()} />
                        </div>

                          {i === messages.length - 1 && (
                            <StudentResponseCard
                              question={question}
                              disabled={isLoading || limitReached}
                              onSubmit={submitStudentAnswer}
                            />
                          )}
                      </div>
                    );
                    })()}
                  </div>
                </div>
                {!isLoading && (
                  <div className="flex items-center gap-2 mt-2">
                    {/* Action buttons only on last assistant message */}
                    {i === messages.length - 1 && (
                      <ChatActionButtons
                        disabled={isLoading || limitReached}
                        onAction={(prompt) => send(prompt)}
                        allowed={extractAssistantMeta(msg.content)?.actions}
                      />
                    )}
                    {/* Voice: full controls on currently-playing message, speaker icon on others */}
                    {voiceEnabled && (() => {
                      const isCurrentlyPlaying = voiceSync.syncState.isPlaying || voiceSync.syncState.isPaused;
                      const isThisMessagePlaying = isCurrentlyPlaying && lastSpokenText === stripAssistantMeta(msg.content);
                      if (isThisMessagePlaying) {
                        return (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <VoicePlaybackControls
                              syncState={voiceSync.syncState}
                              onPause={voiceSync.pause}
                              onResume={voiceSync.resume}
                              onStop={stopSpeaking}
                              onSkipForward={voiceSync.skipForward}
                              onSkipBackward={voiceSync.skipBackward}
                              onReplay={() => playMessage(msg.content)}
                            />
                          </div>
                        );
                      }
                      // Voice button removed — manual control only
                      return null;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              <div className="edu-chat-bubble max-w-[96%] rounded-tr-sm text-primary-foreground"
                style={{ background: "var(--gradient-hero)", boxShadow: "var(--shadow-primary)" }}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            )}
          </div>
          );
        })}

        {isLoading && (messages.length === 0 || messages[messages.length - 1]?.role !== "assistant") && (
          <div className="flex gap-3 animate-fade-in">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center text-lg flex-shrink-0 overflow-hidden" style={{ background: "var(--gradient-hero)" }}><img src={profAvatar} alt="KelasAI" className="h-full w-full object-cover" /></div>
            <div className="edu-chat-bubble max-w-[96%] rounded-tl-sm bg-muted flex items-center gap-2 px-4 py-3">
              <span className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:300ms]" />
              <span className="text-xs text-muted-foreground font-medium ml-1">KelasAI écrit…</span>
            </div>
          </div>
        )}
        {/* Streaming indicator while assistant message is growing */}
        {isLoading && messages.length > 0 && messages[messages.length - 1]?.role === "assistant" && (
          <div className="flex items-center gap-1.5 ml-12 -mt-2 animate-fade-in">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="text-[11px] text-muted-foreground font-medium">KelasAI écrit…</span>
          </div>
        )}
        <div ref={endRef} />

        {/* Floating "scroll to bottom" button when user scrolled up */}
        {!autoFollow && (
          <button
            type="button"
            onClick={() => { setAutoFollow(true); scrollToBottom(true); }}
            className="sticky bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold shadow-lg hover:scale-105 transition-transform animate-fade-in"
            style={{ width: "fit-content", margin: "0 auto" }}
            aria-label="Revenir en bas"
          >
            ↓ Nouveau message
          </button>
        )}
      </div>

      {/* Subscription banner */}
      {user && subscription && subscription.plan === "free" && (
        <div className="px-4 py-2 border-t border-border bg-muted/50 flex items-center justify-between w-full">
          <p className="text-xs text-muted-foreground font-medium">
            {limitReached ? "🔒 Limite atteinte" : `💬 ${subscription.limit - subscription.used}/${subscription.limit} messages · 📷 ${remainingPhotos}/3 photos`}
          </p>
          <button onClick={() => setShowPaymentModal(true)} className="flex items-center gap-1 text-xs font-bold text-primary hover:underline">
            <Crown className="w-3.5 h-3.5" /> Premium
          </button>
        </div>
      )}

      {/* Guest demo banner */}
      {!user && (
        <div className="px-4 py-2 border-t border-border bg-muted/50 flex items-center justify-between w-full">
          <p className="text-xs text-muted-foreground font-medium">
            {guestLimitReached
              ? `🔒 Mode démo : ${GUEST_LIMIT}/${GUEST_LIMIT} questions utilisées`
              : `🎯 Mode démo · ${Math.max(0, GUEST_LIMIT - guestCount)}/${GUEST_LIMIT} questions restantes`}
          </p>
          <button onClick={openSignup} className="flex items-center gap-1 text-xs font-bold text-primary hover:underline">
            Créer un compte gratuit
          </button>
        </div>
      )}

      {/* Input */}
      <div className="sticky bottom-0 z-30 px-4 py-4 border-t border-border bg-card shadow-[0_-4px_20px_-12px_rgba(0,0,0,0.15)] pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {limitReached ? (
          <div className="text-center py-3 max-w-3xl mx-auto">
            <p className="text-sm font-bold text-foreground mb-2">🔒 Limite quotidienne atteinte</p>
            <p className="text-xs text-muted-foreground mb-1">Ton crédit de 10 messages est épuisé.</p>
            <p className="text-xs text-muted-foreground mb-3">⏳ Tes messages se régénèrent automatiquement dans les <strong>24 heures</strong>. Reviens demain ou passe en Premium pour un accès illimité !</p>
            <button onClick={() => setShowPaymentModal(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-bold text-white" style={{ background: "var(--gradient-hero)" }}>
              <Crown className="w-4 h-4" /> Devenir Premium
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingImages.length > 0 && (
              <div className="flex flex-wrap gap-3 rounded-3xl border border-border bg-muted/40 p-3">
                {pendingImages.map((image) => (
                  <div key={image.id} className="relative overflow-hidden rounded-2xl border border-border bg-card">
                    <img src={image.dataUrl} alt={image.name} className="h-20 w-20 object-cover" />
                    <button type="button" onClick={() => removePendingImage(image.id)} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-card/90 text-foreground">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex items-center gap-2 sm:gap-3 bg-muted rounded-2xl px-3 sm:px-4 py-2 sm:py-3">
              <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(event) => { void handlePickImages(event.target.files); event.currentTarget.value = ""; }} />
              {user && subscription && (
                <button type="button" onClick={() => fileInputRef.current?.click()} className="flex h-10 w-10 sm:h-10 sm:w-10 items-center justify-center rounded-2xl border border-border bg-card text-foreground hover:border-primary/30 hover:text-primary transition-colors flex-shrink-0" title={subscription.plan === "premium" ? "Ajouter des photos" : `Mode free : ${remainingPhotos}/3 photos aujourd'hui`}>
                  <Camera className="h-4 w-4" />
                </button>
              )}
              <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder={limitReached ? "🔒 Limite atteinte — Passe en Premium" : guestLimitReached ? `🔒 Mode démo limité à ${GUEST_LIMIT} questions — Crée un compte` : inputPlaceholder}
                className="edu-chat-input flex-1 bg-transparent font-medium text-foreground placeholder:text-muted-foreground outline-none min-w-0" disabled={isLoading || limitReached || guestLimitReached} />
              <button type="button" onClick={toggleVoiceInput} className={`flex h-10 w-10 items-center justify-center rounded-2xl border transition-colors flex-shrink-0 ${isListening ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:border-primary/30 hover:text-primary"}`} title="Dictée vocale" disabled={limitReached || guestLimitReached}>
                {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
              <button type="submit" disabled={limitReached || guestLimitReached || (!input.trim() && pendingImages.length === 0) || isLoading} className="relative w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-90 transition-opacity overflow-hidden" style={{ background: "var(--gradient-hero)" }} title={isLoading ? "KelasAI génère la réponse…" : "Envoyer"}>
                {isLoading ? (
                  <>
                    <span className="absolute inset-0 rounded-xl animate-pulse bg-white/20" />
                    <Loader2 className="w-4 h-4 text-white animate-spin relative z-10" />
                  </>
                ) : (
                  <Send className="w-4 h-4 text-white" />
                )}
              </button>
            </form>
          </div>
        )}
        
      </div>

      {/* Premium Upsell Modal */}
      <PremiumUpsellModal
        open={showPremiumModal}
        onOpenChange={setShowPremiumModal}
        trigger={upsellTrigger}
        remainingMessages={subscription ? Math.max(0, subscription.limit - subscription.used) : undefined}
      />
    </div>
  );
};

export default Chat;
